(() => {
  if (customElements.get('luscentra-refill-upsell')) return;

  const CATEGORY_ORDER = ['capsule', 'microfibre', 'ceramic'];
  const SCENTS = ['neutral', 'citrus', 'lavender', 'jasmine', 'rose'];
  const REQUIRED = {capsule: 1, microfibre: 1, ceramic: 1};
  const normalise = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };

  async function freshCartPayload(drawer, addedVariantId) {
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = drawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = new URL(root, location.origin);
    sectionsUrl.searchParams.set('sections', sectionIds.join(','));
    const [sectionsResponse, cartResponse] = await Promise.all([
      fetch(sectionsUrl.toString(), {headers: {Accept: 'application/json'}, cache: 'no-store'}),
      fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}, cache: 'no-store'}),
    ]);
    if (!sectionsResponse.ok || !cartResponse.ok) throw new Error('Cart refresh failed');
    const [sections, cart] = await Promise.all([sectionsResponse.json(), cartResponse.json()]);
    return {...cart, id: addedVariantId, sections};
  }

  class LuscentraRefillUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mounted === 'true') return;
      this.dataset.mounted = 'true';
      this.content = this.querySelector('[data-refill-upsell-content]');
      this.status = this.querySelector('[data-refill-upsell-status]');
      this.selections = new Map();
      this.addEventListener('change', (event) => event.stopPropagation());

      try {
        this.config = JSON.parse(this.querySelector('[data-refill-upsell-config]')?.textContent || '{}');
      } catch (_) {
        return;
      }

      this.requirements = {...REQUIRED, ...(this.config.requirements || {})};
      this.catalog = this.buildCatalog();
      this.refresh();
    }

    buildCatalog() {
      const products = this.config.products || {};
      const families = {
        handheld: this.emptyFamily(this.config.copy?.handheld || 'Handheld'),
        wallMount: this.emptyFamily(this.config.copy?.wallMount || 'Wall Mount'),
      };

      Object.entries(products.starters || {}).forEach(([familyKey, product]) => {
        (product?.variants || []).forEach((variant) => {
          families[familyKey]?.starterVariantIds.add(Number(variant.id));
        });
      });

      CATEGORY_ORDER.forEach((category) => {
        const product = products[category];
        (product?.variants || []).forEach((variant) => {
          const familyKey = this.familyForVariant(variant);
          if (!familyKey || !families[familyKey]) return;
          const item = {
            id: Number(variant.id),
            sku: variant.sku || '',
            title: product.title || '',
            variantTitle: variant.title || '',
            price: Number(variant.price || 0),
            available: Boolean(variant.available),
            image: this.imageFor(variant, product, category, familyKey),
            scent: category === 'capsule' ? this.scentForVariant(variant) : '',
          };
          families[familyKey].categories[category].push(item);
          families[familyKey].variantLookup.set(item.id, category);
        });
      });

      return families;
    }

    emptyFamily(label) {
      return {
        label,
        starterVariantIds: new Set(),
        categories: {capsule: [], microfibre: [], ceramic: []},
        variantLookup: new Map(),
      };
    }

    familyForVariant(variant) {
      const sku = String(variant.sku || '').toUpperCase();
      const identity = normalise([variant.title, ...(variant.options || [])].join(' '));
      if (/(^|-)HH(-|$)/.test(sku) || identity.includes('handheld') || identity.includes('hand held')) return 'handheld';
      if (/(^|-)WM(-|$)/.test(sku) || identity.includes('wall mount')) return 'wallMount';
      return '';
    }

    scentForVariant(variant) {
      const identity = normalise([variant.title, ...(variant.options || []), variant.sku].join(' '));
      return SCENTS.find((scent) => identity.includes(scent) || identity.endsWith(` ${scent.slice(0, 1)}`)) || '';
    }

    imageFor(variant, product, category, familyKey) {
      if (category === 'capsule') {
        const scent = this.scentForVariant(variant) || 'citrus';
        const model = familyKey === 'wallMount' ? 'wall-mount' : 'handheld';
        if (product.landscape_images?.[`${model}-${scent}`]) return product.landscape_images[`${model}-${scent}`];
      }
      if (typeof variant.featured_image === 'string') return variant.featured_image;
      if (variant.featured_image?.src) return variant.featured_image.src;
      if (typeof product.featured_image === 'string') return product.featured_image;
      return product.featured_image?.src || product.images?.[0] || '';
    }

    async refresh() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}, cache: 'no-store'});
        if (!response.ok) throw new Error('Cart request failed');
        this.cart = await response.json();
        this.render();
      } catch (_) {
        this.hidden = false;
        this.setStatus(this.config.copy?.error || 'We could not load refill recommendations. Please try again.');
      }
    }

    activeFamilies() {
      return Object.entries(this.catalog).filter(([, family]) =>
        (this.cart?.items || []).some((item) => {
          const id = Number(item.variant_id);
          return family.starterVariantIds.has(id) || family.variantLookup.has(id);
        }),
      );
    }

    hasAcceptedBundle() {
      return (this.cart?.items || []).some((item) => {
        const properties = item.properties || {};
        return properties._luscentra_refill_offer === 'refill-set-15'
          && ['pdp-bundle', 'bundle'].includes(properties._luscentra_refill_source);
      });
    }

    familyState(familyKey, family) {
      const items = this.cart?.items || [];
      const hasStarter = items.some((item) => family.starterVariantIds.has(Number(item.variant_id)));
      const counts = {capsule: 0, microfibre: 0, ceramic: 0};
      const originalExisting = {capsule: 0, microfibre: 0, ceramic: 0};

      CATEGORY_ORDER.forEach((category) => {
        let remaining = this.requirements[category];
        items.forEach((item) => {
          if (family.variantLookup.get(Number(item.variant_id)) !== category) return;
          counts[category] += Number(item.quantity || 0);
          if (remaining <= 0) return;
          const included = Math.min(Number(item.quantity || 0), remaining);
          originalExisting[category] += Number(item.original_price || item.price || 0) * included;
          remaining -= included;
        });
      });

      const missing = Object.fromEntries(
        CATEGORY_ORDER.map((category) => [category, Math.max(0, this.requirements[category] - counts[category])]),
      );
      const missingCategories = CATEGORY_ORDER.filter((category) => missing[category] > 0);
      const selected = {};
      let originalValue = CATEGORY_ORDER.reduce((total, category) => total + originalExisting[category], 0);
      let unavailable = false;

      missingCategories.forEach((category) => {
        const variants = family.categories[category];
        const selectedId = this.selections.get(`${familyKey}:${category}`);
        const selectedVariant = variants.find((variant) => variant.id === selectedId);
        const previewVariant = selectedVariant || variants.find((variant) => variant.available) || variants[0];
        selected[category] = selectedVariant || null;
        if (!previewVariant?.available) unavailable = true;
        originalValue += Number(previewVariant?.price || 0) * missing[category];
      });

      const savings = Math.round(originalValue * (Number(this.config.percentage || 15) / 100));
      return {
        familyKey,
        family,
        hasStarter,
        counts,
        missing,
        missingCategories,
        selected,
        complete: missingCategories.length === 0,
        unavailable,
        originalValue,
        savings,
        discountedValue: originalValue - savings,
      };
    }

    render() {
      this.content.replaceChildren();
      this.setStatus('');
      if (this.hasAcceptedBundle()) {
        this.hidden = true;
        return;
      }

      const active = this.activeFamilies()
        .map(([familyKey, family]) => this.familyState(familyKey, family))
        .filter((state) => !state.complete);
      this.hidden = active.length === 0;
      if (!active.length) return;

      active.forEach((state) => {
        this.trackState(state);
        this.content.append(this.offerCard(state));
      });
    }

    trackState(state) {
      window.__luscentraRefillViews = window.__luscentraRefillViews || new Set();
      window.__luscentraRefillCompletion = window.__luscentraRefillCompletion || new Map();
      const key = state.familyKey;
      const previous = window.__luscentraRefillCompletion.get(key);

      if (!state.complete && !window.__luscentraRefillViews.has(key)) {
        window.__luscentraRefillViews.add(key);
        this.pushAnalytics('refill_upsell_viewed', state);
      }
      if (state.complete && previous === false) this.pushAnalytics('refill_upsell_completed', state);
      window.__luscentraRefillCompletion.set(key, state.complete);
    }

    completeCard(state) {
      const card = node('div', 'lus-cart-refill__complete');
      const mark = node('span', 'lus-cart-refill__complete-mark', '✓');
      mark.setAttribute('aria-hidden', 'true');
      const copy = node('div');
      copy.append(node('strong', '', `${state.family.label}: ${this.config.copy.complete}`), node('p', '', this.config.copy.completeText));
      card.append(mark, copy);
      card.setAttribute('data-shower-type', this.analyticsFamily(state.familyKey));
      return card;
    }

    offerCard(state) {
      const details = node('details', 'lus-cart-refill__offer');
      details.open = true;
      details.setAttribute('data-shower-type', this.analyticsFamily(state.familyKey));
      const summary = node('summary', 'lus-cart-refill__summary');
      const summaryCopy = node('span');
      summaryCopy.append(
        node('span', 'lus-cart-refill__model', `${state.family.label} ${this.config.copy.refills}`),
        node('strong', '', state.missingCategories.length < 3 ? this.config.copy.almostHeading : this.config.copy.heading),
      );
      summary.append(summaryCopy, node('span', 'lus-cart-refill__badge', this.config.copy.discountBadge));
      details.append(summary);

      const body = node('div', 'lus-cart-refill__body');
      body.append(
        node('p', 'lus-cart-refill__intro', state.hasStarter ? this.config.copy.starterText : this.config.copy.refillText),
        node('p', 'lus-cart-refill__progress', `${state.missingCategories.length} ${this.config.copy.remaining}`),
      );

      const rows = node('div', 'lus-cart-refill__rows');
      state.missingCategories.forEach((category) => rows.append(this.recommendationRow(state, category)));
      body.append(rows, this.pricing(state), this.bundleButton(state));
      details.append(body);
      return details;
    }

    recommendationRow(state, category) {
      const variants = state.family.categories[category];
      const selected = state.selected[category];
      const displayVariant = selected || variants.find((variant) => variant.available) || variants[0];
      const row = node('div', 'lus-cart-refill__row');
      const media = node('div', 'lus-cart-refill__media');
      if (displayVariant?.image) {
        const image = document.createElement('img');
        image.src = displayVariant.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 72;
        image.height = 72;
        media.append(image);
      }

      const info = node('div', 'lus-cart-refill__info');
      info.append(
        node('strong', '', this.config.copy[category]),
        node('span', '', `${this.config.copy.quantity} ${state.missing[category]}`),
      );

      if (category === 'capsule') info.append(this.scentSelect(state, variants));

      const controls = node('div', 'lus-cart-refill__controls');
      controls.append(node('span', 'lus-cart-refill__item-price', this.formatMoney(Number(displayVariant?.price || 0) * state.missing[category])));
      const addButton = node('button', 'lus-cart-refill__add', displayVariant?.available ? this.config.copy.add : this.config.copy.unavailable);
      addButton.type = 'button';
      addButton.setAttribute('aria-label', `${this.config.copy.add} ${state.missing[category]} ${this.config.copy[category]}`);
      addButton.disabled = !displayVariant?.available || (category === 'capsule' && !selected);
      addButton.addEventListener('click', () => {
        const variant = category === 'capsule' ? state.selected[category] : displayVariant;
        if (!variant) return;
        this.addItems(
          [{id: variant.id, quantity: state.missing[category], properties: this.lineProperties(state, 'individual')}],
          state,
          'refill_upsell_item_added',
          category,
        );
      });
      controls.append(addButton);
      row.append(media, info, controls);
      return row;
    }

    scentSelect(state, variants) {
      const wrapper = node('label', 'lus-cart-refill__scent');
      wrapper.append(node('span', '', this.config.copy.chooseScent));
      const select = document.createElement('select');
      const placeholder = node('option', '', this.config.copy.scentPlaceholder);
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.selected = !state.selected.capsule;
      select.append(placeholder);

      variants.forEach((variant) => {
        const option = node('option', '', this.scentLabel(variant));
        option.value = String(variant.id);
        option.disabled = !variant.available;
        option.selected = state.selected.capsule?.id === variant.id;
        select.append(option);
      });

      select.addEventListener('change', () => {
        this.selections.set(`${state.familyKey}:capsule`, Number(select.value));
        this.render();
      });
      wrapper.append(select);
      return wrapper;
    }

    scentLabel(variant) {
      if (variant.scent) return variant.scent.charAt(0).toUpperCase() + variant.scent.slice(1);
      return variant.variantTitle;
    }

    pricing(state) {
      const pricing = node('div', 'lus-cart-refill__pricing');
      const regular = node('p');
      regular.append(node('span', '', this.config.copy.refillValue), node('s', '', this.formatMoney(state.originalValue)));
      const offer = node('p');
      offer.append(node('span', '', this.config.copy.offerTotal), node('strong', '', this.formatMoney(state.discountedValue)));
      pricing.append(regular, offer, node('p', 'lus-cart-refill__saving', `${this.config.copy.save} ${this.formatMoney(state.savings)} today · ${Number(this.config.percentage || 15)}% off`));
      return pricing;
    }

    bundleButton(state) {
      const button = node('button', 'button button--full-width lus-cart-refill__bundle', this.config.copy.bundleCta);
      button.type = 'button';
      const capsuleMissing = state.missing.capsule > 0;
      button.disabled = state.unavailable || (capsuleMissing && !state.selected.capsule);
      button.addEventListener('click', () => {
        const items = state.missingCategories.map((category) => {
          const variants = state.family.categories[category];
          const variant = category === 'capsule'
            ? state.selected.capsule
            : variants.find((item) => item.available) || variants[0];
          return variant
            ? {id: variant.id, quantity: state.missing[category], properties: this.lineProperties(state, 'bundle')}
            : null;
        }).filter(Boolean);
        if (items.length !== state.missingCategories.length) return;
        this.addItems(items, state, 'refill_upsell_bundle_added', state.missingCategories.join(','));
      });
      return button;
    }

    lineProperties(state, source) {
      return {
        _luscentra_refill_offer: 'refill-set-15',
        _luscentra_refill_source: source,
        _luscentra_shower_type: this.analyticsFamily(state.familyKey),
      };
    }

    async addItems(items, state, eventName, recommendedProduct) {
      this.setStatus(this.config.copy.adding);
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button, select').forEach((control) => { control.disabled = true; });

      try {
        const drawer = this.closest('cart-drawer');
        const sectionIds = drawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-drawer', 'cart-icon-bubble'];
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items, sections: sectionIds, sections_url: window.location.pathname}),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart request failed');

        this.pushAnalytics(eventName, state, {
          recommended_product: recommendedProduct,
          capsule_scent: state.selected.capsule?.scent || '',
        });

        const freshPayload = await freshCartPayload(drawer, items[0]?.id);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {source: 'refill-upsell', cartData: freshPayload});
        }

        if (drawer?.renderContents) {
          drawer.setActiveElement?.(document.activeElement);
          drawer.renderContents(freshPayload);
        }
      } catch (_) {
        this.removeAttribute('aria-busy');
        this.render();
        this.setStatus(this.config.copy.error);
      }
    }

    pushAnalytics(event, state, extra = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event,
        shower_type: this.analyticsFamily(state.familyKey),
        recommended_product: state.missingCategories.join(','),
        original_refill_value: state.originalValue / 100,
        discounted_refill_value: state.discountedValue / 100,
        savings: state.savings / 100,
        cart_value: Number(this.cart?.total_price || 0) / 100,
        currency: this.config.currency,
        ...extra,
      });
    }

    analyticsFamily(familyKey) {
      return familyKey === 'wallMount' ? 'wall-mount' : 'handheld';
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: this.config.currency || 'INR',
          maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
        }).format(Number(cents) / 100);
      } catch (_) {
        return `${this.config.currency || 'INR'} ${(Number(cents) / 100).toFixed(2)}`;
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }
  }

  customElements.define('luscentra-refill-upsell', LuscentraRefillUpsell);
})();
